import { Component, HostListener, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { BibliothequesService } from 'src/services/bibliotheques.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { BibliothequePilotageModel } from 'src/models/pilotage/bibliothequePilotageModel';
import { DateFormatPipe } from "../../../pipes/date-format.pipe";
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DeleteBibliothequeDialogComponent } from './delete-bibliotheque-dialog/delete-bibliotheque-dialog.component';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';

@Component({
    selector: 'app-bibliotheques-managment',
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        TranslateModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        DateFormatPipe,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatSelectModule,
        NgxMatSelectSearchModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ],
    templateUrl: './bibliotheques-managment.component.html',
    styleUrl: './bibliotheques-managment.component.scss'
})
export class BibliothequesManagmentComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  bibliotheques: BibliothequePilotageModel[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';

  dataSource = new MatTableDataSource<BibliothequePilotageModel>([]);
  displayedColumns: string[] = ['nom', 'totalPackages', 'dateCreation', 'actions'];

  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50];

  searchFilterCtrl: FormControl = new FormControl();
  protected _onDestroy = new Subject<void>();

  constructor(
    public bibliothequesService: BibliothequesService,
    private utilisateurService: UtilisateurService,
    public translate: TranslateService,
    private router: Router,
    private dialog: MatDialog
  ) {
    translate.setDefaultLang('fr');
  }

  ngOnInit(): void {
    this.loadBibliotheques();

    this.searchFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.applyFilters();
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (data: BibliothequePilotageModel, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'nom':
          const nomValue = this.getBibliothequeLocalizedName(data)?.toLowerCase() || '';
          return nomValue;
        case 'totalPackages':
          return data.totalPackages;
        case 'dateCreation':
          const dateValue = new Date(data.dateCreation).getTime();
          return dateValue;
        default:
          return (data as any)[sortHeaderId];
      }
    };

    this.dataSource.filterPredicate = (data: BibliothequePilotageModel, filter: string) => {
      try {
        const filterObject = JSON.parse(filter);
        const searchMatch = !filterObject.searchTerm || (
          this.getBibliothequeLocalizedName(data).toLowerCase().includes(filterObject.searchTerm.toLowerCase()) ||
          data.totalPackages.toString().includes(filterObject.searchTerm.toLowerCase()) ||
          data.dateCreation.toString().toLowerCase().includes(filterObject.searchTerm.toLowerCase())
        );
        return searchMatch;
      } catch {
        return true;
      }
    };
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  paginer(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  toggleMenu(index: number): void {
    this.dataSource.data.forEach((pack, i) => {
      pack.isMenuOpen = (i === index) ? !pack.isMenuOpen : false;
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMenu = target.closest('.menu-container');
    if (!isInsideMenu) {
      this.dataSource.data.forEach(pack => pack.isMenuOpen = false);
    }
  }

  private loadBibliotheques(): void {
    this.isLoading = true;
    const novoEntiteId = this.utilisateurService.entiteUtilisateurSelectionnee.novoEntiteId;

    this.bibliothequesService.obtenirBibliotheques().subscribe(
      (res) => {
        this.bibliotheques = res;
        this.dataSource.data = this.bibliotheques;
        this.applyFilters();
        this.isLoading = false;

        setTimeout(() => {
          if (this.sort && this.paginator) {
            this.dataSource.sort = this.sort;
            this.dataSource.paginator = this.paginator;
          }
        });
      },
      (error) => {
        console.error('Error loading libraries:', error);
        this.isLoading = false;
      }
    );
  }


  applyFilters(): void {
    const filterValue = {
      searchTerm: this.searchTerm
    };
    this.dataSource.filter = JSON.stringify(filterValue);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  reinitialiserFiltre(): void {
    this.searchTerm = '';
    this.searchFilterCtrl.reset();
    this.applyFilters();
  }

  editBibliotheque(bibliotheque: BibliothequePilotageModel): void {
    this.router.navigate(['/bibliotheques/edit', bibliotheque.bibliothequeId]);
  }

  createBibliotheque(): void {
    this.router.navigate(['/bibliotheques/create']);
  }

  openDeleteDialog(bibliotheque: BibliothequePilotageModel): void {
    const dialogRef = this.dialog.open(DeleteBibliothequeDialogComponent, {
      width: '600px',
      data: { bibliothequeId: bibliotheque.bibliothequeId }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadBibliotheques();
      }
    });
  }

  getBibliothequeLocalizedName(bibliotheque: BibliothequePilotageModel): string {
    return this.bibliothequesService.getNomLocalise(bibliotheque.locales || {});
  }
}
